import { useMutation } from '@apollo/client';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../../../core/components/Button.tsx';
import MultiSelectField from '../../../../core/components/Fields/MultiSelectField.tsx';
import FormField from '../../../../core/components/FormField.tsx';
import Modal from '../../../../core/components/Modal.tsx';
import { useToaster } from '../../../../core/components/Toast.tsx';
import { UPDATE_AGENT } from '../../queries.ts';

interface AddAssistantMailboxModalProps {
    agent: any;
    availableSmartInboxTriggers: { id: string; name: string; agentIds: string[] }[];
    onClose: () => void;
    onAdded?: () => Promise<unknown> | void;
    assignedIds?: string[];
    onConfirm?: (ids: string[]) => void;
}

const AddAssistantMailboxModal = ({
    agent,
    availableSmartInboxTriggers,
    onClose,
    onAdded,
    assignedIds: assignedIdsOverride,
    onConfirm,
}: AddAssistantMailboxModalProps) => {
    const { t } = useTranslation('assistants');
    const { publishToast } = useToaster();
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [updateAgent, { loading }] = useMutation(UPDATE_AGENT);

    const assignedIds = useMemo(
        () => assignedIdsOverride ?? (agent?.smartInboxIntegrations || []).map((integration) => integration.id),
        [agent, assignedIdsOverride]
    );

    const options = useMemo(
        () =>
            availableSmartInboxTriggers
                .filter((trigger) => !assignedIds.includes(trigger.id))
                .toSorted((a, b) => a.name.localeCompare(b.name))
                .map((trigger) => ({
                    value: trigger.id,
                    label: trigger.name,
                    // a mailbox feeds one assistant at a time; saving here moves it over
                    description: trigger.agentIds?.some((id) => id !== agent?.id)
                        ? t('detail.addMailboxModal.assignedElsewhere')
                        : undefined,
                })),
        [availableSmartInboxTriggers, assignedIds, agent, t]
    );

    const handleAdd = async () => {
        if (onConfirm) {
            onConfirm(selectedIds);
            onClose();
            return;
        }
        try {
            await updateAgent({
                variables: { id: agent.id, input: { triggerIds: [...assignedIds, ...selectedIds] } },
            });
            await onAdded?.();
            const firstName = options.find((option) => option.value === selectedIds[0])?.label;
            publishToast({
                title: t('detail.addMailboxModal.successTitle'),
                description: t('detail.addMailboxModal.successDescription', {
                    count: selectedIds.length,
                    mailbox: firstName,
                }),
                status: 'success',
            });
            onClose();
        } catch (error) {
            console.error(error);
            publishToast({ description: t('detail.addMailboxModal.error'), status: 'error' });
        }
    };

    return (
        <Modal
            title={t('detail.addMailboxModal.title')}
            onClose={onClose}
            className="sm:max-w-[40rem]"
            buttons={[
                <Button key="cancel" type="button" onClick={onClose}>
                    {t('detail.addMailboxModal.cancel')}
                </Button>,
                <Button
                    key="add"
                    type="button"
                    variant="primary"
                    disabled={selectedIds.length === 0}
                    loading={loading}
                    onClick={handleAdd}
                >
                    {t('detail.addMailboxModal.add')}
                </Button>,
            ]}
        >
            <div className="flex flex-col gap-4 text-sm">
                <p className="text-content-medium">{t('detail.addMailboxModal.description')}</p>
                <FormField label={t('detail.addMailboxModal.fieldLabel')}>
                    <MultiSelectField
                        options={options}
                        value={selectedIds}
                        onValueChange={setSelectedIds}
                        placeholder={t('detail.addMailboxModal.placeholder')}
                        emptyMessage={t('detail.addMailboxModal.empty')}
                        removeLabel={t('detail.addMailboxModal.remove')}
                        aria-label={t('detail.addMailboxModal.fieldLabel')}
                    />
                </FormField>
            </div>
        </Modal>
    );
};

export default AddAssistantMailboxModal;
